import { createContext, useContext, useEffect, useState, type FormEvent } from 'react';
import '../site.css';

/**
 * Das Schaufenster der Plattform unter evercourse.de – Aufbau und Tonfall wie evercast.video (Rainer, 03.10.2026).
 * Positionierung (Rainer, 03.10. abends): Evercourse ist eine Videokurs-Plattform für die eigene Akademie wie andere
 * auch – aber (1) nicht so teuer, (2) einfach, überfrachtet nicht mit Werkzeugen, und (3) **mit KI steuerbar**
 * (Claude & Co. erledigen die Verwaltung über die Schnittstelle). Das ist der USP. Nicht „dein Server/dein Haus“ –
 * die Anbieter mieten bei uns. Selbstbewusst, ohne Einkommensversprechen, nur Funktionen, die es gibt.
 * Reihenfolge: Hero → drei Vorteile → Zahlen → Problem (Menüs) → KI-Steuerung → drei Schritte → Produkt → Funktionen →
 * große Plattform vs. Evercourse → Weniger Technik → Zwei Akademien → Zielgruppe → Funktionsumfang → FAQ → Abschluss.
 *
 * Die Seiten werden beim Build vorgerendert (entry-server.tsx) – deshalb hier nichts, was `window` beim Rendern braucht.
 */

/** Was Evercourse heute kann – der tatsächliche Funktionsumfang, nichts Geplantes. */
const FEATURES: { title: string; items: string[] }[] = [
  {
    title: 'Steuerung per KI',
    items: [
      'Schnittstelle mit API-Schlüsseln für deinen KI-Assistenten (Claude, ChatGPT & Co.)',
      'Rechte-Stufen je Schlüssel: nur lesen, Inhalte, Kunden, Einstellungen',
      'Protokoll, wer was über die Schnittstelle geändert hat',
      'Alles, was die KI anlegt, siehst und änderst du auch in der Verwaltung',
    ],
  },
  {
    title: 'Kurse und Lektionen',
    items: [
      'Beliebig viele Kurse, Module und Lektionen',
      'Kapitel mit Sprungmarken aus dem Workbook',
      'Workbooks und Dateien zum Herunterladen je Lektion',
      'Module sofort, nach Tagen oder ab Datum freischalten',
      'Vorschau-Lektionen und Gratis-Kurse',
    ],
  },
  {
    title: 'Deine Akademie',
    items: [
      'Eigene Domain oder Adresse unter evercourse.de',
      'Dein Logo, deine Farben, hell oder dunkel, deine Schrift',
      'Web-App mit eigenem Icon auf dem Handy',
      'Impressum und Datenschutz in deinem Namen',
    ],
  },
  {
    title: 'Video',
    items: [
      'Upload aus dem Browser, auch große Dateien, fortsetzbar',
      'Aufbereitung in bis zu drei Qualitätsstufen (bis 1080p)',
      'Auslieferung von Servern in Deutschland',
      'Geschützte Video-Adressen, die nur Kunden öffnen können',
    ],
  },
  {
    title: 'Kunden und Zugang',
    items: [
      'Anmeldung ohne Passwort: Link oder Code per Mail',
      'Fortschritt je Lektion, „Weiterlernen“ auf der Startseite',
      'Lektionen werden beim Ansehen automatisch als erledigt markiert',
      'Ein Zugang gilt für zwei Geräte zur selben Zeit',
      'Notizen der Kunden je Lektion',
    ],
  },
  {
    title: 'Verkauf',
    items: [
      'Produkte mit Preis, die einen oder mehrere Kurse freischalten',
      'Gesperrte Kurse zeigen Schloss und Preis in der Bibliothek',
      'Zugang von Hand vergeben – für Bestandskunden oder Geschenke',
      'Widerrufsbelehrung mit Häkchen vor dem Kauf',
      'Keine Provision auf deine Verkäufe',
    ],
  },
  {
    title: 'E-Mails',
    items: ['Anmelde-Link, Willkommen, Modul-Freischaltung, Erinnerung, Abschluss', 'Alle Texte anpassbar, mit Platzhaltern und Vorschau', 'Eigene Absenderadresse'],
  },
  {
    title: 'Betrieb und Datenschutz',
    items: ['Server in Deutschland, kein Tracking, kein Cookie-Banner', 'Tägliche Sicherung', 'Keine KI-Bausteine in der Plattform – deine Daten trainieren nichts', 'Persönlicher Support'],
  },
];

/** Ziel des Knopfs „Mehr erfahren“ im Kasten „Das kann Evercourse“ – die Erfolgsmagneten-Community. */
const COMMUNITY_URL = 'https://www.erfolgsmagneten.com';

export const TRIAL_PATH = '/kostenlos-testen';
export const LANDING_TITLE = 'Evercourse – Deine Akademie. Einfach. Bezahlbar. Mit KI gesteuert.';

function TrialButton({ label = 'Kostenlos testen', large }: { label?: string; large?: boolean }) {
  return (
    <a className={`button${large ? ' button--large' : ''}`} href={TRIAL_PATH}>
      {label}
    </a>
  );
}

const NoCard = () => <p className="site__note">Keine Kreditkarte erforderlich.</p>;

/** Screenshots lassen sich anklicken und groß ansehen – Rainers Wunsch (03.10.). Bild, Beschriftung, Schließen per Klick oder Esc. */
type Shot = { src: string; alt: string; caption?: string };
const LightboxContext = createContext<(shot: Shot) => void>(() => undefined);

function Lightbox({ shot, onClose }: { shot: Shot | null; onClose: () => void }) {
  useEffect(() => {
    if (!shot) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [shot, onClose]);
  if (!shot) return null;
  return (
    <div className="site__lightbox" role="dialog" aria-modal="true" aria-label={shot.caption ?? shot.alt} onClick={onClose}>
      <button type="button" className="site__lightbox-close" aria-label="Schließen" onClick={onClose}>
        ×
      </button>
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={shot.src} alt={shot.alt} />
        {shot.caption && <figcaption>{shot.caption}</figcaption>}
      </figure>
    </div>
  );
}

/** Ein anklickbarer Screenshot. Ohne JavaScript bleibt es ein normales Bild. */
function ShotImage({ src, alt, caption, className, width, height, loading }: Shot & { className?: string; width?: number; height?: number; loading?: 'lazy' }) {
  const open = useContext(LightboxContext);
  return (
    <button type="button" className={`site__shot${className ? ` ${className}` : ''}`} onClick={() => open({ src, alt, caption: caption ?? alt })} aria-label={`${alt} – vergrößern`}>
      <img src={src} alt={alt} width={width} height={height} loading={loading} />
    </button>
  );
}

export function LandingPage() {
  const [shot, setShot] = useState<Shot | null>(null);
  useEffect(() => {
    document.title = LANDING_TITLE;
  }, []);

  return (
    <LightboxContext.Provider value={setShot}>
    <div className="site">
      <Lightbox shot={shot} onClose={() => setShot(null)} />
      <SiteHeader />

      {/* 1. Hero */}
      <section className="site__hero">
        <h1>
          Deine Akademie. Einfach. Bezahlbar. <em>Mit KI gesteuert.</em>
        </h1>
        <p className="site__sub">Die Videokurs-Plattform, die du von Claude & Co. einrichten lässt – statt dich durch Menüs zu klicken.</p>
        <p className="site__lead">
          Evercourse hat alles, was Videokurse brauchen: Bibliothek, Lektionen, Workbooks, Fortschritt, Zugänge – unter deiner Adresse, im Look deiner
          Marke. Und eine Schnittstelle, über die dein KI-Assistent die Verwaltung für dich erledigt.
        </p>
        <p className="site__claim">Du sagst, was du willst. Deine KI richtet es ein.</p>
        <div className="site__actions">
          <TrialButton large />
          <a className="button button--large button--secondary" href="#ki">
            So funktioniert das mit der KI
          </a>
        </div>
        <NoCard />
        <figure className="site__devices">
          <ShotImage className="site__desktop" src="/site/bibliothek.png" alt="Die Kursbibliothek einer Akademie: Weiterlernen-Kachel und freigeschaltete Kurse" caption="Kursbibliothek – Erfolgsmagneten Akademie (Desktop)" width={1800} height={1125} />
          <ShotImage className="site__phone" src="/site/bibliothek-handy.png" alt="Dieselbe Bibliothek auf dem Handy" caption="Kursbibliothek – Balance Vital Dogs Akademie (Handy)" width={462} height={1000} />
        </figure>
      </section>

      {/* 2. Drei Vorteile */}
      <section className="site__block">
        <div className="site__split">
          <div className="site__split-text">
            <h2>Eine Kursplattform wie jede andere. Nur einfacher, günstiger – und mit KI.</h2>
            <div className="site__prose">
              <p>Du musst keine neue Art zu unterrichten lernen. Kurse, Lektionen, Workbooks, Zugänge – alles, was du von anderen Plattformen kennst, ist da.</p>
              <p className="site__stack">
                <span>Ohne hundert Werkzeuge, die du nie anfasst.</span>
                <span>Ohne Preis, der mit jedem Kunden wächst.</span>
                <strong>Und ohne jede Einstellung selbst anklicken zu müssen.</strong>
              </p>
            </div>
          </div>
          <img className="site__photo" src="/site/foto-aufnahme.jpg" alt="Eine Kursleiterin nimmt an ihrem Schreibtisch mit Kamera und Ringlicht eine Lektion auf" loading="lazy" />
        </div>
        <div className="site__benefit-grid">
          <Benefit icon="sparkle" title="Mit KI steuerbar" text="Dein Assistent legt Kurse an, schreibt Beschreibungen, setzt Bilder, Preise und Zugänge – über eine Schnittstelle mit genau den Rechten, die du erlaubst." />
          <Benefit icon="simple" title="Einfach" text="Genau die Funktionen, die Videokurse brauchen. Keine Funnel-Baukästen, keine Community-Module, kein Dschungel aus Menüs." />
          <Benefit icon="coins" title="Bezahlbar" text="Ein fairer Monatspreis für deine Akademie – und keine Provision auf deine Verkäufe. Egal, wie viele Kurse und Kunden du hast." />
        </div>
      </section>

      {/* 3. Zahlen */}
      <section className="site__block site__block--dark">
        <h2>Alles, was eine Kursplattform braucht. Nichts, was dich aufhält.</h2>
        <div className="site__numbers">
          <div>
            <strong>0 %</strong>
            <span>Provision auf deine Verkäufe. Der Umsatz bleibt bei dir.</span>
          </div>
          <div>
            <strong>1 Satz</strong>
            <span>an deine KI reicht, um einen Kurs mit Lektionen, Texten und Preis anzulegen.</span>
          </div>
          <div>
            <strong>100 %</strong>
            <span>deine Marke: eigene Domain, eigenes Logo, eigene Farben – kein Plattform-Logo bei deinen Kunden.</span>
          </div>
          <div>
            <strong>0</strong>
            <span>Tracker, Cookie-Banner und KI-Bausteine in der Plattform. Deine Daten trainieren nichts.</span>
          </div>
        </div>
      </section>

      {/* 4. Problem */}
      <section className="site__block site__block--tight">
        <div className="site__split site__split--reverse">
          <div className="site__split-text">
            <h2>Warum Stunden in Menüs verbringen, statt zu unterrichten?</h2>
            <div className="site__prose">
              <p className="site__stack">
                <span>Du klickst dich durch Einstellungen.</span>
                <span>Du suchst die richtige Option.</span>
                <span>Du tippst Beschreibungen, lädst Bilder hoch, prüfst Preise.</span>
                <span>Und beim nächsten Kurs beginnt alles wieder von vorne.</span>
              </p>
              <p>
                <strong>Damit ist Schluss.</strong>
              </p>
              <p>Mit Evercourse sagst du deinem KI-Assistenten, was du brauchst. Er legt an, du prüfst und gibst frei.</p>
            </div>
          </div>
          <img className="site__photo" src="/site/foto-lernen.jpg" alt="Ein Kunde sieht sich abends auf dem Sofa eine Lektion auf dem Tablet an" loading="lazy" />
        </div>
        <div className="site__three">
          <div>
            <h3>Weniger Klicks. Mehr Kurs.</h3>
            <p>Kurse, Lektionen, Texte, Bilder, Preise – ein Auftrag an die KI statt dreißig Formulare.</p>
          </div>
          <div>
            <h3>Weniger Werkzeuge. Mehr Übersicht.</h3>
            <p>Sieben Bereiche in der Verwaltung, nicht siebzig. Du findest alles beim ersten Mal.</p>
          </div>
          <div>
            <h3>Weniger Kosten. Mehr Marge.</h3>
            <p>Fester Monatspreis, keine Provision – dein Umsatz bleibt dein Umsatz.</p>
          </div>
        </div>
      </section>

      {/* 5. KI-Steuerung – der USP */}
      <section id="ki" className="site__block">
        <h2>Deine KI bedient die Verwaltung. Du bleibst der Chef.</h2>
        <div className="site__prose">
          <p>
            In Evercourse erzeugst du einen Schlüssel für deinen KI-Assistenten – Claude, ChatGPT oder was du sonst benutzt – und legst fest, was er
            darf: nur lesen, Inhalte anlegen, Kunden freischalten, Einstellungen ändern. Dann redest du mit deiner KI wie mit einer Assistentin.
          </p>
        </div>
        <div className="site__chat">
          <div className="site__chat-msg site__chat-msg--you">
            <span>Du</span>
            <p>
              Leg in meiner Akademie den Kurs „Hundephysiotherapie für Zuhause“ an: 8 Lektionen, zu jeder ein kurzer Beschreibungstext aus meinen
              Notizen, ein passendes Vorschaubild, Preis 149 €. Das zweite Modul soll 7 Tage nach dem Kauf aufgehen.
            </p>
          </div>
          <div className="site__chat-msg site__chat-msg--ai">
            <span>Deine KI</span>
            <p>
              Erledigt: Kurs angelegt, 8 Lektionen mit Texten, Bilder gesetzt, Produkt „Hundephysiotherapie für Zuhause“ für 149 € erstellt, Modul 2 auf
              „7 Tage nach Zugang“. Noch als Entwurf – soll ich veröffentlichen?
            </p>
          </div>
        </div>
        <div className="site__three">
          <div>
            <h3>Du gibst frei.</h3>
            <p>Alles, was die KI anlegt, steht als Entwurf in deiner Verwaltung. Veröffentlicht wird, wenn du es sagst.</p>
          </div>
          <div>
            <h3>Du bestimmst die Rechte.</h3>
            <p>Jeder Schlüssel hat eine Rechte-Stufe. Löschen und Zahlungs-Einstellungen bleiben dir vorbehalten, wenn du willst.</p>
          </div>
          <div>
            <h3>Du siehst alles.</h3>
            <p>Ein Protokoll zeigt, wer was über die Schnittstelle geändert hat – Schlüssel für Schlüssel.</p>
          </div>
        </div>
        <p className="site__claim">Evercourse selbst enthält keine KI. Du bringst deine mit – und deine Daten trainieren nichts.</p>
      </section>

      {/* 6. Drei Schritte */}
      <section id="ablauf" className="site__steps">
        <h2>Einmal einrichten. Immer wieder unterrichten.</h2>
        <ol className="site__step-list">
          <li>
            <span className="site__step-num">1</span>
            <h3>Akademie bekommen</h3>
            <p>Deine Adresse, dein Logo, deine Farben – wir richten die Akademie ein, du bekommst den Zugang zur Verwaltung.</p>
          </li>
          <li>
            <span className="site__step-num">2</span>
            <h3>KI verbinden</h3>
            <p>In der Verwaltung erzeugst du einen Schlüssel für deinen Assistenten und legst fest, was er darf.</p>
          </li>
          <li>
            <span className="site__step-num">3</span>
            <h3>Kurse anlegen lassen</h3>
            <p>Texte, Lektionen, Bilder, Preise übernimmt die KI. Videos lädst du im Browser hoch. Dann freigeben – fertig.</p>
          </li>
        </ol>
        <p className="site__steps-claim">
          Du nimmst den Kurs einmal auf.
          <br />
          Den Rest erledigt deine KI mit Evercourse.
        </p>
        <div className="site__actions">
          <TrialButton label="Akademie starten" large />
        </div>
      </section>

      {/* 7. Produkt */}
      <section id="produkt" className="site__block">
        <h2>So sieht deine Akademie mit Evercourse aus.</h2>
        <div className="site__demo">
          <figure>
            <ShotImage src="/site/bibliothek.png" alt="Die Kursbibliothek mit Weiterlernen-Kachel" caption="Bibliothek: Weiterlernen-Kachel und freigeschaltete Kurse" loading="lazy" />
            <figcaption>
              <h3>Aus Käufern werden Lernende.</h3>
              <p>Die Bibliothek zeigt, was freigeschaltet ist – und wo es weitergeht.</p>
            </figcaption>
          </figure>
          <figure>
            <ShotImage src="/site/kurs.png" alt="Die Kursseite mit Kennzahlen und Lektionen" caption="Kursseite: Hero, Kennzahlen, Lektionen als Kacheln" loading="lazy" />
            <figcaption>
              <h3>Aus einem Kurs wird ein Weg.</h3>
              <p>Hero, Kennzahlen, Lektionen als Kacheln – mit Fortschritt je Lektion.</p>
            </figcaption>
          </figure>
          <figure>
            <ShotImage src="/site/lektion.png" alt="Eine Lektion mit Sprungmarken aus dem Workbook" caption="Lektion im Fenster mit Sprungmarken und Workbook" loading="lazy" />
            <figcaption>
              <h3>Aus Zuschauen wird Lernen.</h3>
              <p>Lektion im Fenster, Sprungmarken, Workbook, „Als gesehen markieren“.</p>
            </figcaption>
          </figure>
          <figure>
            <ShotImage src="/site/verwaltung-kurs.png" alt="Die Verwaltung eines Kurses" caption="Verwaltung: ein Kurs mit Modulen und Lektionen" loading="lazy" />
            <figcaption>
              <h3>Aus Arbeit wird Übersicht.</h3>
              <p>Sieben Bereiche, eine Verwaltung – von Hand oder per KI.</p>
            </figcaption>
          </figure>
        </div>
        <div className="site__actions">
          <TrialButton label="Evercourse ausprobieren" large />
        </div>
      </section>

      {/* 8. Funktionen in Ergebnissprache */}
      <section id="funktionen" className="site__block">
        <h2>Alles drin, was deine Kunden zum Lernen brauchen.</h2>
        <div className="site__benefit-grid">
          <Benefit icon="library" title="Eine Bibliothek. Alle Kurse." text="Freigeschaltete Kurse oben, gesperrte mit Schloss und Preis darunter. Wer einen Kurs hat, sieht sofort den nächsten." />
          <Benefit icon="bookmark" title="Zur richtigen Minute." text="Sprungmarken aus dem Workbook führen direkt in die Stelle im Video. Workbooks und Dateien liegen bei der Lektion." />
          <Benefit icon="progress" title="Weiterlernen statt Suchen." text="Fortschritt wird gespeichert – auf jedem Gerät. Lektionen gelten als erledigt, sobald sie gesehen wurden." />
          <Benefit icon="calendar" title="Module zur richtigen Zeit." text="Sofort, nach einigen Tagen oder ab einem Datum: Du bestimmst, wann welches Modul aufgeht." />
          <Benefit icon="key" title="Anmelden ohne Passwort." text="Ein Link oder ein Code per Mail – kein vergessenes Passwort, kein Support-Aufwand. Zwei Geräte gleichzeitig." />
          <Benefit icon="mail" title="E-Mails, die du bestimmst." text="Willkommen, Freischaltung, Erinnerung, Abschluss – alle Texte anpassbar, mit Vorschau, in deinem Namen." />
        </div>
      </section>

      {/* 9. Große Plattform vs. Evercourse */}
      <section className="site__block">
        <h2>Groß und teuer – oder genau richtig?</h2>
        <div className="site__versus">
          <div className="site__versus-col">
            <h3>Große Kursplattform</h3>
            <ul>
              <li>Dutzende Werkzeuge, die du nie benutzt</li>
              <li>Hoher Monatspreis oder Provision pro Verkauf</li>
              <li>Jede Einstellung von Hand durch die Menüs</li>
              <li>Logo der Plattform bei deinen Kunden</li>
            </ul>
          </div>
          <div className="site__versus-col site__versus-col--us">
            <h3>Evercourse</h3>
            <ul>
              <li>Genau das, was Videokurse brauchen</li>
              <li>Fairer Monatspreis, keine Provision</li>
              <li>Verwaltung per KI – oder von Hand, wenn du magst</li>
              <li>Deine Domain, dein Logo, deine Farben</li>
            </ul>
          </div>
        </div>
        <p className="site__claim">Deine Kurse wachsen. Dein Verwaltungsaufwand nicht.</p>
      </section>

      {/* 10. Weniger Technik */}
      <section className="site__block site__block--dark">
        <h2>Unterrichten statt Kursplattform studieren.</h2>
        <div className="site__prose">
          <p>Evercourse ist dafür gebaut, schnell einsatzbereit zu sein.</p>
        </div>
        <ol className="site__simple">
          <li>Akademie bekommen.</li>
          <li>Schlüssel erzeugen.</li>
          <li>KI anweisen.</li>
          <li>Videos hochladen.</li>
          <li>Freigeben.</li>
        </ol>
        <p className="site__claim">Fertig.</p>
        <div className="site__prose">
          <p>Keine unnötige Technik. Kein kompliziertes System. Sondern eine Akademie, die für dich unterrichtet.</p>
        </div>
      </section>

      {/* 11. Zwei Akademien – hell und dunkel */}
      <section className="site__block">
        <h2>Zwei Akademien. Zwei Marken. Eine Plattform.</h2>
        <div className="site__prose">
          <p>Hell oder dunkel, Gold oder Blau, serifenlos oder rund – jede Akademie trägt ihr eigenes Gesicht. Die Technik dahinter ist dieselbe.</p>
        </div>
        <div className="site__pair">
          <figure>
            <ShotImage src="/site/kurs.png" alt="Eine Kursseite im dunklen Erscheinungsbild mit Gold-Akzenten" caption="Dunkel mit Gold – Erfolgsmagneten Akademie" loading="lazy" />
            <figcaption>Dunkel mit Gold – Erfolgsmagneten Akademie</figcaption>
          </figure>
          <figure>
            <ShotImage src="/site/kurs-hell.png" alt="Eine Kursseite im hellen Erscheinungsbild mit Blau" caption="Hell mit Blau – Balance Vital Dogs Akademie" loading="lazy" />
            <figcaption>Hell mit Blau – Balance Vital Dogs Akademie</figcaption>
          </figure>
        </div>
      </section>

      {/* 12. Zielgruppe */}
      <section className="site__block site__block--soft">
        <div className="site__split">
          <img className="site__photo" src="/site/foto-praxis.jpg" alt="Eine Hundephysiotherapeutin behandelt in ihrer Praxis einen Australian Shepherd" loading="lazy" />
          <div className="site__split-text">
            <h2>Für Experten, die unterrichten wollen – nicht Software bedienen.</h2>
            <div className="site__prose">
              <p>Evercourse ist für Coaches, Berater, Trainer, Praxen, Hundeschulen und alle, die etwas können, das andere lernen wollen – und die ihre Zeit lieber in Inhalte stecken als in Einstellungen.</p>
              <p className="site__claim">
                Dein Wissen bleibt persönlich.
                <br />
                Deine Verwaltung wird zum Gespräch.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 13. Funktionsumfang – wie bei Evercast: nicht kaufbar, Projekt der Erfolgsmagneten */}
      <section id="funktionsumfang" className="site__block">
        <h2>Was kostet dich eine Plattform, die du selbst bedienen musst?</h2>
        <div className="site__price">
          <div className="site__price-head">
            <img className="site__logo site__logo--price" src="/site/logo.png" alt="Evercourse" width={1910} height={282} loading="lazy" />
            <h3>Das kann Evercourse</h3>
          </div>
          <div className="site__price-groups">
            {FEATURES.map((group) => (
              <div key={group.title}>
                <h3>{group.title}</h3>
                <ul className="site__check">
                  {group.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="site__price-notice">
            <p>
              <strong>Evercourse kann nicht gekauft werden.</strong> Evercourse ist ein KI-Projekt der Erfolgsmagneten. Wir zeigen dir, wie einfach es
              heutzutage ist, deine eigene Kursplattform zu bauen – und mit KI zu steuern. Wenn auch du mehr dazu wissen willst, komm in die
              Erfolgsmagneten-Community.
            </p>
            <a className="button button--large" href={COMMUNITY_URL} rel="noopener">
              Mehr erfahren
            </a>
          </div>
        </div>
      </section>

      {/* 14. FAQ */}
      <section id="faq" className="site__faq">
        <h2>Häufige Fragen</h2>
        <Faq q="Welche KI kann ich mit Evercourse benutzen?" a="Jede, die mit einer Schnittstelle arbeiten kann – Claude, ChatGPT und andere Assistenten. Du erzeugst in der Verwaltung einen Schlüssel und gibst ihn deinem Assistenten. Eine fertige Anbindung für Claude ist in Arbeit." />
        <Faq q="Kann die KI etwas kaputt machen?" a="Nein, wenn du es nicht erlaubst. Jeder Schlüssel hat eine Rechte-Stufe, Löschen bleibt dir vorbehalten, und alles Neue steht als Entwurf in deiner Verwaltung, bis du es veröffentlichst. Ein Protokoll zeigt jede Änderung." />
        <Faq q="Steckt in Evercourse selbst KI?" a="Nein. Evercourse enthält keine KI-Bausteine, deine Daten und Videos trainieren kein Modell. Die KI ist deine eigene – Evercourse stellt nur die Schnittstelle." />
        <Faq q="Muss ich meine Kurse mit Evercourse aufnehmen?" a="Nein. Du nutzt deine vorhandenen Videos – als MP4 oder MOV. Evercourse bereitet sie in bis zu drei Qualitätsstufen auf." />
        <Faq q="Brauche ich eine eigene Domain?" a="Nein, aber du kannst eine haben. Ohne eigene Domain läuft deine Akademie unter deinname.evercourse.de, mit eigener Domain unter deiner Adresse – das Zertifikat holt Evercourse selbst." />
        <Faq q="Wie melden sich meine Kunden an?" a="Ohne Passwort. Sie geben ihre E-Mail-Adresse ein und bekommen einen Anmelde-Link oder einen sechsstelligen Code. Ein Zugang gilt für zwei Geräte zur selben Zeit." />
        <Faq q="Kann ich Evercourse kostenlos testen?" a="Ja. Ohne Kreditkarte. Du bekommst eine eigene Akademie und probierst alles mit deinem eigenen Kurs aus – von Hand oder mit deiner KI." />
      </section>

      {/* 15. Abschluss */}
      <section className="site__cta">
        <h2>Warum alles selbst einstellen, wenn deine KI es für dich tun kann?</h2>
        <p className="site__cta-sub">Deine Akademie. Einfach. Bezahlbar. Mit KI gesteuert.</p>
        <p>Mach aus deinem besten Kurs eine Akademie, die unter deinem Namen unterrichtet – und lass die Verwaltung deine KI machen.</p>
        <TrialButton label="Evercourse kostenlos testen" large />
        <p className="site__cta-note">Keine Kreditkarte erforderlich.</p>
      </section>

      <SiteFooter />
    </div>
    </LightboxContext.Provider>
  );
}

/** Ziel aller „Kostenlos testen“-Knöpfe: Name, E-Mail, Thema – landet in der Hausverwaltung. */
export function TrialPage() {
  useEffect(() => {
    document.title = 'Kostenlos testen – Evercourse';
  }, []);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__trial">
        <h1>Evercourse kostenlos testen</h1>
        <p className="site__lead">Du bekommst eine eigene Akademie und probierst Evercourse mit deinem eigenen Kurs aus – ohne Kreditkarte, ohne Laufzeit.</p>
        <ol className="site__trial-steps">
          <li>
            <strong>Du sagst uns kurz, wer du bist</strong>
            <span>und was du unterrichten möchtest.</span>
          </li>
          <li>
            <strong>Wir richten deine Akademie ein</strong>
            <span>und spielen dein erstes Video ein, wenn du magst – meist am selben Werktag.</span>
          </li>
          <li>
            <strong>Du testest alles in Ruhe</strong>
            <span>Bibliothek, Kurs, Lektionen, Workbooks, Mails, Verwaltung – von Hand oder mit deiner KI.</span>
          </li>
        </ol>
        <InquiryForm />
      </main>
      <SiteFooter />
    </div>
  );
}

/** Das Formular. Token-Zeitfalle und Honigtopf gegen Programme (Konzept 3.7); ohne Token nimmt der Server nichts an. */
function InquiryForm() {
  const [token, setToken] = useState('');
  const [form, setForm] = useState({ name: '', email: '', topic: '', message: '', website: '' });
  const [state, setState] = useState<{ kind: 'idle' } | { kind: 'busy' } | { kind: 'done' } | { kind: 'error'; message: string }>({ kind: 'idle' });

  useEffect(() => {
    fetch('/api/plattform/formular', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { token?: string } | null) => setToken(d?.token ?? ''))
      .catch(() => setToken(''));
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ kind: 'busy' });
    try {
      const r = await fetch('/api/plattform/anfrage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...form, token }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { message?: string };
        setState({ kind: 'error', message: body.message ?? 'Das hat nicht geklappt. Bitte versuche es noch einmal.' });
        return;
      }
      setState({ kind: 'done' });
    } catch {
      setState({ kind: 'error', message: 'Keine Verbindung. Bitte versuche es gleich noch einmal.' });
    }
  };

  if (state.kind === 'done') {
    return (
      <div className="site__done">
        <h2>Danke, {form.name.trim().split(/\s+/)[0] || 'deine Anfrage ist da'}!</h2>
        <p>Wir melden uns meist am selben Werktag bei {form.email.trim()} und richten deine Akademie ein.</p>
      </div>
    );
  }

  return (
    <form className="site__form" onSubmit={(e) => void submit(e)}>
      <label>
        Dein Name
        <input type="text" name="name" autoComplete="name" required minLength={2} maxLength={120} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
      </label>
      <label>
        Deine E-Mail-Adresse
        <input type="email" name="email" autoComplete="email" required maxLength={254} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
      </label>
      <label>
        Was möchtest du unterrichten?
        <input type="text" name="topic" required minLength={2} maxLength={300} placeholder="z. B. Hundephysiotherapie für Zuhause" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} />
      </label>
      <label>
        Noch etwas, das wir wissen sollten? (optional)
        <textarea name="message" maxLength={2000} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
      </label>
      {/* Honigtopf: für Menschen unsichtbar, Programme füllen ihn aus. */}
      <label className="site__form-trap" aria-hidden="true">
        Website
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} />
      </label>
      {state.kind === 'error' && <div className="site__form-error">{state.message}</div>}
      <div className="site__actions">
        <button type="submit" className="button button--large" disabled={state.kind === 'busy'}>
          {state.kind === 'busy' ? 'Wird gesendet …' : 'Testzugang anfragen'}
        </button>
      </div>
      <p className="site__note" style={{ textAlign: 'left' }}>
        Wir verwenden deine Angaben nur, um deinen Testzugang einzurichten und dir zu antworten.{' '}
        <a href="/datenschutz" style={{ color: 'inherit' }}>
          Datenschutz
        </a>
      </p>
    </form>
  );
}

export function LegalPage({ kind }: { kind: 'impressum' | 'datenschutz' }) {
  useEffect(() => {
    document.title = `${kind === 'impressum' ? 'Impressum' : 'Datenschutz'} – Evercourse`;
  }, [kind]);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__legal">{kind === 'impressum' ? <Impressum /> : <Datenschutz />}</main>
      <SiteFooter />
    </div>
  );
}

/** Unbekannte Adresse auf der Plattform. */
export function SiteNotFound() {
  useEffect(() => {
    document.title = 'Seite nicht gefunden – Evercourse';
  }, []);
  return (
    <div className="site">
      <SiteHeader />
      <main className="site__trial">
        <h1>Diese Seite gibt es nicht.</h1>
        <p className="site__lead">Vielleicht hilft die Startseite weiter.</p>
        <div className="site__actions">
          <a className="button button--large" href="/">
            Zur Startseite
          </a>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function SiteHeader() {
  return (
    <header className="site__bar">
      <a className="site__brand" href="/" aria-label="Evercourse – zur Startseite">
        <img className="site__logo" src="/site/logo.png" alt="Evercourse" width={1910} height={282} />
      </a>
      <nav className="site__nav">
        <a href="/#ablauf">So funktioniert es</a>
        <a href="/#produkt">Ansehen</a>
        <a href="/#funktionsumfang">Funktionen</a>
        <a href="/#faq">Fragen</a>
        <a className="button button--small" href={TRIAL_PATH}>
          Kostenlos testen
        </a>
      </nav>
    </header>
  );
}

function SiteFooter() {
  return (
    <footer className="site__footer">
      <img className="site__logo site__logo--footer" src="/site/logo.png" alt="Evercourse" width={1910} height={282} loading="lazy" />
      <div className="site__footer-links">
        <span>© {new Date().getFullYear()} Evercourse · ein Projekt von Erfolgsmagneten</span>
        <a href="https://evercast.video" rel="noopener">
          Evercast – Webinare
        </a>
        <a href="/impressum">Impressum</a>
        <a href="/datenschutz">Datenschutz</a>
      </div>
    </footer>
  );
}

type IconName = 'sparkle' | 'simple' | 'coins' | 'library' | 'bookmark' | 'progress' | 'calendar' | 'key' | 'mail';

function Benefit({ icon, title, text }: { icon: IconName; title: string; text: string }) {
  return (
    <div className="site__benefit">
      <span className="site__icon" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <h3>{title}</h3>
      <p>{text}</p>
    </div>
  );
}

function Icon({ name }: { name: IconName }) {
  const common = { width: 26, height: 26, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const };
  switch (name) {
    case 'sparkle':
      return (
        <svg {...common}>
          <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z" />
          <path d="M19 16l.8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8zM5 2l.6 1.6L7.2 4.2 5.6 4.8 5 6.4 4.4 4.8 2.8 4.2l1.6-.6z" />
        </svg>
      );
    case 'simple':
      return (
        <svg {...common}>
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M8 12h8" />
        </svg>
      );
    case 'coins':
      return (
        <svg {...common}>
          <ellipse cx="12" cy="6" rx="8" ry="3" />
          <path d="M4 6v6c0 1.7 3.6 3 8 3s8-1.3 8-3V6M4 12v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
        </svg>
      );
    case 'library':
      return (
        <svg {...common}>
          <rect x="3" y="4" width="5" height="16" rx="1" />
          <rect x="10" y="4" width="5" height="16" rx="1" />
          <path d="M17.5 5l3.5 1-3 14-3.5-1z" />
        </svg>
      );
    case 'bookmark':
      return (
        <svg {...common}>
          <path d="M6 3h12v18l-6-4-6 4z" />
          <path d="M9 9h6" />
        </svg>
      );
    case 'progress':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 3a9 9 0 019 9h-9z" fill="currentColor" stroke="none" opacity="0.7" />
          <path d="M8.5 12.5l2.5 2.5 4.5-5" />
        </svg>
      );
    case 'calendar':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M3 10h18M8 3v4M16 3v4M9 15l2 2 4-4" />
        </svg>
      );
    case 'key':
      return (
        <svg {...common}>
          <circle cx="8" cy="14" r="4.5" />
          <path d="M11.5 10.5L20 2M16 6l2.5 2.5M13.5 8.5L16 11" />
        </svg>
      );
    case 'mail':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="M3 7l9 6 9-6" />
        </svg>
      );
  }
}

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`site__faq-item${open ? ' is-open' : ''}`}>
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open}>
        {q}
        <span aria-hidden="true">{open ? '–' : '+'}</span>
      </button>
      {open && <p>{a}</p>}
    </div>
  );
}

/** Angaben von Rainer (19.09.2026, wie evercast.video). Adresse und Register nur hier ändern. */
function Impressum() {
  return (
    <>
      <h1>Impressum</h1>
      <p className="muted">Angaben gemäß § 5 DDG</p>
      <h2>Anbieter</h2>
      <p>
        Erfolgsmagneten GmbH
        <br />
        Leopoldstr. 244
        <br />
        80807 München
      </p>
      <p>
        Vertreten durch den Geschäftsführer: Rainer Stolte
        <br />
        Registergericht: Amtsgericht München · HRB 295540
      </p>
      <h2>Kontakt</h2>
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
      <p>Rainer Stolte, Anschrift wie oben.</p>
      <h2>Umsatzsteuer</h2>
      <p>Umsatzsteuer-Identifikationsnummer gemäß § 27a UStG: DE370363054</p>
      <h2>Streitbeilegung</h2>
      <p>Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.</p>
    </>
  );
}

/**
 * Datenschutzerklärung für evercourse.de, aus der Evercast-Fassung (19.09.2026) auf Kurse übertragen. Sie beschreibt,
 * was die Plattform tatsächlich tut: keine Cookies und kein Tracking auf der öffentlichen Seite; in den Akademien ein
 * Sitzungs-Cookie nach der Anmeldung, Fortschritts-Erfassung beim Ansehen, Gerätegrenze; Mails über Brevo; Hosting und
 * Sicherung bei IONOS. Wer den Code ändert, prüft, ob dieser Text noch stimmt. Zahlung über Stripe ist angekündigt,
 * nicht beschrieben – kommt mit der Einbindung dazu.
 */
function Datenschutz() {
  const controller = (
    <p>
      Erfolgsmagneten GmbH
      <br />
      Leopoldstr. 244
      <br />
      80807 München
      <br />
      Deutschland
    </p>
  );
  return (
    <>
      <h1>Datenschutzerklärung</h1>
      <p className="muted">Stand: 3. Oktober 2026</p>
      <p>
        Der Schutz personenbezogener Daten ist uns wichtig. Nachfolgend informieren wir darüber, welche personenbezogenen Daten beim Besuch unserer Website
        evercourse.de, bei der Kontaktaufnahme mit uns und bei der Nutzung von Evercourse verarbeitet werden.
      </p>
      <p>
        Dabei unterscheiden wir zwischen der öffentlich zugänglichen Evercourse-Website und der eigentlichen Evercourse-Plattform, mit der unsere Kunden
        (im Folgenden „Anbieter“) eigene Akademien mit Videokursen betreiben. Weder auf der Website noch in der Plattform setzen wir Analyse- oder
        Marketingtechnologien ein. Es gibt kein Werbetracking, keine Cookies für Werbezwecke und kein Cookie-Banner, weil es nichts gibt, in das eingewilligt
        werden müsste. Kunden-, Kurs- und Anbieterdaten werden weder für Werbung oder Marketingprofile noch für das Training von KI-Modellen verwendet.
      </p>

      <h2>1. Verantwortlicher</h2>
      <p>Verantwortlicher im Sinne der Datenschutz-Grundverordnung (DSGVO) für die Website evercourse.de und die eigenen Datenverarbeitungen im Zusammenhang mit Evercourse ist:</p>
      {controller}
      <p>
        Telefon: <a href="tel:+498924413422">+49 (0) 89 244 134 22</a>
        <br />
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
        <br />
        Web:{' '}
        <a href="https://www.erfolgsmagneten.com" rel="noopener">
          www.erfolgsmagneten.com
        </a>
      </p>
      <p>
        Registergericht: Amtsgericht München · Handelsregisternummer: HRB 295540
        <br />
        Geschäftsführer: Rainer Stolte
        <br />
        Umsatzsteuer-Identifikationsnummer: DE370363054
      </p>
      <p>Evercourse ist ein Projekt der Erfolgsmagneten GmbH.</p>

      <h2>2. Unterschied zwischen Evercourse-Website und Evercourse-Plattform</h2>
      <h3>Öffentliche Website</h3>
      <p>
        Die Website evercourse.de dient der Information über Evercourse, der Vorstellung der Funktionen sowie der Gewinnung und Betreuung von Interessenten
        und Anbietern. Für die Verarbeitung personenbezogener Daten auf dieser Website ist die Erfolgsmagneten GmbH verantwortlich. Auf der Website werden
        keine Cookies gesetzt und keine Analyse- oder Marketingdienste eingesetzt.
      </p>
      <h3>Evercourse-Plattform</h3>
      <p>
        Evercourse wird von Coaches, Beratern, Trainern, Praxen und anderen Anbietern genutzt, um Videokurse mit Lektionen, Workbooks und Fortschritt
        bereitzustellen. Jeder Anbieter erhält dafür eine eigene Akademie unter einer eigenen Adresse (zum Beispiel name.evercourse.de oder eine eigene
        Domain).
      </p>
      <p>
        Soweit ein Anbieter personenbezogene Daten seiner Kunden innerhalb seiner Akademie verarbeitet, entscheidet der Anbieter über Zweck und Mittel
        dieser Verarbeitung und ist damit datenschutzrechtlich Verantwortlicher. Die Erfolgsmagneten GmbH verarbeitet diese Daten im Rahmen der
        Bereitstellung von Evercourse im Auftrag des jeweiligen Anbieters gemäß Art. 28 DSGVO.
      </p>
      <p>
        Wenn Sie sich also als Kunde in einer Akademie anmelden, die ein Anbieter über Evercourse betreibt, ist für diese Datenverarbeitung in erster Linie
        der betreffende Anbieter verantwortlich. Informationen über Rechtsgrundlage und konkrete Verwendung Ihrer Daten erhalten Sie von diesem Anbieter;
        sein Impressum und seine Datenschutzerklärung stehen im Fuß jeder Seite seiner Akademie.
      </p>

      <h2>3. Hosting und Datensicherung</h2>
      <p>Unsere Website sowie die technische Infrastruktur von Evercourse werden bei der</p>
      <p>
        IONOS SE
        <br />
        Elgendorfer Straße 57
        <br />
        56410 Montabaur
        <br />
        Deutschland
      </p>
      <p>
        auf Servern in Deutschland betrieben. IONOS stellt uns Server-, Hosting- und Speicherleistungen zur Verfügung. Auch die Videos der Anbieter werden
        von unseren eigenen Servern bei IONOS ausgeliefert; ein externes Content Delivery Network wird nicht eingesetzt. Tägliche Sicherungskopien der
        Datenbank und der Dateien werden nach 30 Tagen automatisch gelöscht.
      </p>
      <p>
        Beim Aufruf unserer Website und der Plattform werden technisch erforderliche Informationen verarbeitet: IP-Adresse des aufrufenden Geräts, Datum und
        Uhrzeit des Zugriffs, aufgerufene Adresse, Browsertyp und -version sowie technische Statusinformationen. Diese Angaben stehen in Server-Protokollen,
        die fortlaufend überschrieben werden und in der Regel nur wenige Tage vorhanden sind.
      </p>
      <p>
        Die Verarbeitung ist erforderlich, um Website und Plattform technisch bereitzustellen, einen sicheren Betrieb zu gewährleisten, Fehler zu erkennen
        und Angriffe oder missbräuchliche Zugriffe abzuwehren. Zur Abwehr automatisierter Anmeldungen und Anfragen zählen wir Anfragen je IP-Adresse für die
        Dauer einer Stunde im Arbeitsspeicher; eine dauerhafte Speicherung der IP-Adresse zu einer Anmeldung erfolgt nicht.
      </p>
      <p>
        Rechtsgrundlage ist Art. 6 Abs. 1 lit. f DSGVO. Unser berechtigtes Interesse liegt im sicheren, stabilen und zuverlässigen Betrieb unserer Website
        und unserer technischen Systeme. Soweit IONOS personenbezogene Daten in unserem Auftrag verarbeitet, erfolgt dies auf Grundlage von Art. 28 DSGVO.
      </p>

      <h2>4. TLS-Verschlüsselung</h2>
      <p>
        Unsere Website und die Evercourse-Plattform werden ausschließlich verschlüsselt übertragen (TLS). Eine verschlüsselte Verbindung erkennen Sie
        daran, dass die Adresse im Browser mit „https://“ beginnt.
      </p>

      <h2>5. Cookies und Speicherung im Browser</h2>
      <p>Auf der öffentlichen Website evercourse.de setzen wir keine Cookies und speichern nichts im Browser. Ein Cookie-Banner ist deshalb nicht erforderlich.</p>
      <p>
        In den Akademien der Anbieter setzen wir ein technisch erforderliches Sitzungs-Cookie, sobald sich ein Kunde angemeldet hat; es erkennt den
        angemeldeten Kunden für bis zu 90 Tage wieder und wird beim Abmelden gelöscht. Im geschützten Verwaltungsbereich der Anbieter und in der
        Hausverwaltung der Plattform gibt es ebenfalls je ein Sitzungs-Cookie. Der Zugriff auf das Endgerät erfolgt auf Grundlage von § 25 Abs. 2 TDDDG, die
        damit verbundene Verarbeitung personenbezogener Daten auf Grundlage von Art. 6 Abs. 1 lit. b DSGVO.
      </p>
      <p>Statistik-, Reichweiten- oder Marketingtechnologien setzen wir nicht ein.</p>

      <h2>6. Kontaktaufnahme und Testzugang</h2>
      <p>
        Wenn Sie über das Formular „Kostenlos testen“ auf evercourse.de, per E-Mail oder telefonisch mit uns Kontakt aufnehmen, verarbeiten wir die von
        Ihnen übermittelten Daten zur Bearbeitung Ihres Anliegens: Name, E-Mail-Adresse, das Thema, das Sie unterrichten möchten, und Ihre Nachricht. Die
        Angaben aus dem Formular werden in unserer Datenbank gespeichert und per E-Mail an uns weitergeleitet.
      </p>
      <p>
        Ist Ihre Kontaktaufnahme auf den Abschluss oder die Durchführung eines Vertrages gerichtet, erfolgt die Verarbeitung auf Grundlage von Art. 6 Abs. 1
        lit. b DSGVO. Bei sonstigen Anfragen erfolgt sie auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO; unser berechtigtes Interesse besteht in der
        Bearbeitung und Beantwortung der an uns gerichteten Kommunikation.
      </p>

      <h2>7. Zugänge der Anbieter</h2>
      <p>
        Für die Nutzung von Evercourse richten wir für jeden Anbieter eine Akademie mit Zugang zur Verwaltung ein. Dabei verarbeiten wir Name, E-Mail-Adresse
        und Passwort (nur als kryptografischer Hash gespeichert) der Verwalter, den Namen der Akademie, die Absenderadresse für E-Mails, Logo und
        Erscheinungsbild sowie die vom Anbieter hochgeladenen Videos, Dateien, Texte und Produkte. Fehlgeschlagene Anmeldeversuche an der Verwaltung werden
        mit IP-Adresse protokolliert, um Angriffe zu erkennen. Legt ein Anbieter API-Schlüssel an, speichern wir davon nur einen Hash und protokollieren,
        welche Änderungen über den Schlüssel vorgenommen wurden.
      </p>
      <p>
        Diese Daten benötigen wir zur Einrichtung und Verwaltung des Zugangs, zur Erbringung unserer vertraglichen Leistungen, zur Kommunikation sowie zur
        Abrechnung. Rechtsgrundlage ist Art. 6 Abs. 1 lit. b DSGVO; für handels- und steuerrechtliche Aufbewahrung Art. 6 Abs. 1 lit. c DSGVO.
      </p>

      <h2>8. Anmeldung und Lernen in einer Akademie</h2>
      <p>Wenn Sie als Kunde eine Akademie nutzen, die ein Anbieter über Evercourse betreibt, werden folgende Daten verarbeitet:</p>
      <ul>
        <li>Ihre E-Mail-Adresse und Ihr Name (soweit angegeben) sowie die Ihnen zugeordneten Kurse (Zugänge und Käufe);</li>
        <li>
          Anmelde-Links und Codes, die Ihnen per E-Mail zugeschickt werden und 15 Minuten gültig sind, sowie die daraus entstehende Sitzung (bis zu 90 Tage);
        </li>
        <li>
          Ihr Lernfortschritt: welche Lektionen Sie angesehen haben, an welcher Stelle Sie zuletzt waren und ob eine Lektion als erledigt gilt – damit Sie
          auf jedem Gerät weiterlernen können;
        </li>
        <li>
          beim Ansehen regelmäßig die Position im Video und eine zufällige Kennung des geöffneten Browsers, damit ein Zugang nur auf zwei Geräten zur selben
          Zeit genutzt wird; diese Kennung wird bei jedem Öffnen neu erzeugt;
        </li>
        <li>Notizen, die Sie zu einer Lektion speichern;</li>
        <li>die E-Mails, die Ihnen die Akademie schickt (Anmeldung, Willkommen, Freischaltung eines Moduls, Erinnerung, Abschluss) samt Versandzeitpunkt.</li>
      </ul>
      <p>
        Für diese Verarbeitung ist der Anbieter verantwortlich; sie dient der Bereitstellung der Kurse, die Sie erworben oder angefordert haben (Art. 6
        Abs. 1 lit. b DSGVO). Ein Werbeversand findet über Evercourse nicht statt.
      </p>

      <h2>9. E-Mail-Versand über Brevo</h2>
      <p>Für den Versand der E-Mails aus Evercourse (Anmelde-Links und Codes, Willkommen, Modul-Freischaltung, Erinnerungen, Abschluss, Benachrichtigungen an uns) setzen wir die</p>
      <p>
        Brevo (Sendinblue GmbH)
        <br />
        Köpenicker Straße 126
        <br />
        10179 Berlin
        <br />
        Deutschland
      </p>
      <p>
        als Dienstleister ein. An Brevo werden die für die Zustellung erforderlichen Daten übermittelt, insbesondere E-Mail-Adresse, Name, Betreff und Inhalt
        der Nachricht. Brevo ist vertraglich nach Art. 28 DSGVO gebunden und verarbeitet die Daten in der Europäischen Union.
      </p>

      <h2>10. Kein Werbetracking, keine KI-Nutzung</h2>
      <p>
        Weder auf evercourse.de noch in den Akademien der Anbieter setzen wir Werbetracking ein, insbesondere kein Meta-Pixel und keine Webanalyse. Kunden-,
        Kurs-, Anbieter- und Videodaten werden durch die Erfolgsmagneten GmbH nicht dazu verwendet, Werbeprofile zu erstellen, personalisierte Werbung
        auszuspielen oder Marketingzielgruppen aufzubauen. Diese Daten werden auch nicht zum Training eigener oder fremder KI-Modelle verwendet; Evercourse
        enthält keine KI-Bausteine.
      </p>

      <h2>11. Empfänger und Dienstleister</h2>
      <p>
        Wir geben personenbezogene Daten nur weiter, wenn hierfür eine Rechtsgrundlage besteht oder dies zur Erbringung unserer Leistungen erforderlich ist.
        Empfänger können Hosting- und IT-Dienstleister (IONOS SE), der Dienstleister für den E-Mail-Versand (Brevo / Sendinblue GmbH), Steuerberater,
        Zahlungsdienstleister nach deren zukünftiger Einbindung sowie Behörden sein, soweit wir gesetzlich zu einer Übermittlung verpflichtet sind.
        Dienstleister, die Daten ausschließlich in unserem Auftrag verarbeiten, werden entsprechend Art. 28 DSGVO eingebunden.
      </p>

      <h2>12. Datenübermittlungen außerhalb der EU und des EWR</h2>
      <p>
        Alle von uns eingesetzten Dienstleister verarbeiten die Daten in Deutschland beziehungsweise in der Europäischen Union. Eine Übermittlung
        personenbezogener Daten in Drittländer findet durch uns nicht statt.
      </p>

      <h2>13. Speicherdauer</h2>
      <p>Wir speichern personenbezogene Daten nur so lange, wie dies für den jeweiligen Verarbeitungszweck erforderlich ist.</p>
      <p>
        Kundendaten in einer Akademie bleiben gespeichert, solange der Zugang besteht; Kunden können ihr Konto in der Akademie löschen lassen, Anbieter können
        Kunden entfernen. Anmelde-Links verfallen nach 15 Minuten, Sitzungen nach 90 Tagen. Daten aus Kontaktanfragen werden gelöscht, sobald die Anfrage
        abschließend bearbeitet wurde und keine gesetzlichen, vertraglichen oder berechtigten Gründe für eine weitere Speicherung bestehen. Vertrags- und
        Abrechnungsdaten werden entsprechend den handels- und steuerrechtlichen Aufbewahrungspflichten gespeichert. Sicherungskopien werden nach 30 Tagen
        überschrieben.
      </p>

      <h2>14. Widerspruch gegen Verarbeitung aufgrund berechtigter Interessen</h2>
      <p>
        Soweit wir personenbezogene Daten auf Grundlage von Art. 6 Abs. 1 lit. f DSGVO verarbeiten, haben Sie nach Art. 21 DSGVO das Recht, aus Gründen, die
        sich aus Ihrer besonderen Situation ergeben, jederzeit Widerspruch gegen diese Verarbeitung einzulegen. Wir verarbeiten die betreffenden Daten
        anschließend nicht mehr, es sei denn, wir können zwingende schutzwürdige Gründe für die Verarbeitung nachweisen, die Ihre Interessen, Rechte und
        Freiheiten überwiegen, oder die Verarbeitung dient der Geltendmachung, Ausübung oder Verteidigung von Rechtsansprüchen.
      </p>

      <h2>15. Ihre Datenschutzrechte</h2>
      <p>
        Nach Maßgabe der gesetzlichen Voraussetzungen haben Sie das Recht auf Auskunft gemäß Art. 15 DSGVO, Berichtigung gemäß Art. 16 DSGVO, Löschung gemäß
        Art. 17 DSGVO, Einschränkung der Verarbeitung gemäß Art. 18 DSGVO sowie Datenübertragbarkeit gemäß Art. 20 DSGVO. Zur Wahrnehmung Ihrer Rechte
        können Sie sich an uns wenden:
      </p>
      {controller}
      <p>
        E-Mail: <a href="mailto:info@erfolgsmagneten.com">info@erfolgsmagneten.com</a>
      </p>
      <p>
        Betrifft Ihr Anliegen Daten in der Akademie eines Anbieters, wenden Sie sich bitte zuerst an diesen Anbieter; wir unterstützen ihn bei der
        Beantwortung. Sie haben außerdem das Recht, sich bei einer Datenschutz-Aufsichtsbehörde zu beschweren, etwa beim Bayerischen Landesamt für
        Datenschutzaufsicht.
      </p>

      <h2>16. Änderungen dieser Datenschutzerklärung</h2>
      <p>
        Wir passen diese Datenschutzerklärung an, wenn sich Evercourse oder die Rechtslage ändert – etwa mit der Einbindung eines Zahlungsdienstleisters. Es
        gilt die jeweils hier veröffentlichte Fassung.
      </p>
    </>
  );
}
